import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Office, Client, Event, LegalFile, Role } from '../types';
import { SystemPresetData } from './SystemPresetContext';
import { fetchOfficesByLawyerID } from '../services/officeService';
import { fetchClientsByRoleIDs } from '../services/clientService';
import { fetchEventsByLawyerID } from '../services/eventService';
import { getLegalFilesByLawyerID } from '../services/legalFileService';
import { useAuth } from './AuthContext';
import { useUserRoles } from './UserRolesContext';
import { LocalUsageService, LocalUsageStats } from '../services/localUsageService';

export interface CacheData {
  offices: Office[];
  clients: Client[];
  events: Event[];
  legalFiles: LegalFile[];
  systemPresetData: SystemPresetData | null;
  roles: Role[];
  usageStats: LocalUsageStats | null;
}

export interface CacheProgress {
  total: number;
  completed: number;
  current: string;
  percentage: number;
}

export interface DetailedLoadingState {
  userProfile: boolean;
  roles: boolean;
  offices: boolean;
  clients: boolean;
  events: boolean;
  legalFiles: boolean;
  systemPresetData: boolean;
  isComplete: boolean;
}

interface CacheContextType {
  // Data
  cache: CacheData;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Progress
  progress: CacheProgress;
  detailedLoading: DetailedLoadingState;
  
  // Actions
  loadCache: () => Promise<void>;
  refreshCache: () => Promise<void>;
  invalidateCache: (keys: (keyof CacheData)[]) => void;
  clearAllCache: () => void;
  
  // Individual data getters
  getOffices: () => Office[];
  getClients: (officeId?: string) => Client[];
  getEvents: (filters?: { status?: string; date?: string }) => Event[];
  getLegalFiles: (clientId?: string) => LegalFile[];
  getSystemPresetData: () => SystemPresetData | null;
  getRoles: () => Role[];
  getUsageStats: () => LocalUsageStats | null;
  calculateUsageStats: () => LocalUsageStats | null;
  
  // Data mutations
  addOffice: (office: Office) => void;
  updateOffice: (officeId: string, updates: Partial<Office>) => void;
  removeOffice: (officeId: string) => void;
  
  addClient: (client: Client) => void;
  updateClient: (clientId: string, updates: Partial<Client>) => void;
  removeClient: (clientId: string) => void;
  
  addEvent: (event: Event) => void;
  updateEvent: (eventId: string, updates: Partial<Event>) => void;
  removeEvent: (eventId: string) => void;
  
  addLegalFile: (legalFile: LegalFile) => void;
  updateLegalFile: (legalFileId: string, updates: Partial<LegalFile>) => void;
  removeLegalFile: (legalFileId: string) => void;
}

const CacheContext = createContext<CacheContextType | undefined>(undefined);

export const useCache = () => {
  const context = useContext(CacheContext);
  if (context === undefined) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
};

interface CacheProviderProps {
  children: ReactNode;
}

export const CacheProvider: React.FC<CacheProviderProps> = ({ children }) => {
  console.log('🔍 Debug - CacheProvider component rendered');
  
  const { user } = useAuth();
  const { roles } = useUserRoles();
  
  console.log('🔍 Debug - CacheProvider hooks initialized');
  console.log('🔍 Debug - User in CacheProvider:', user);
  console.log('🔍 Debug - Roles in CacheProvider:', roles);
  
  // Cache state
  const [cache, setCache] = useState<CacheData>({
    offices: [],
    clients: [],
    events: [],
    legalFiles: [],
    systemPresetData: null,
    roles: [],
    usageStats: null
  });
  
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Progress state
  const [progress, setProgress] = useState<CacheProgress>({
    total: 0,
    completed: 0,
    current: '',
    percentage: 0
  });

  // Detailed loading state
  const [detailedLoading, setDetailedLoading] = useState<DetailedLoadingState>({
    userProfile: false,
    roles: false,
    offices: false,
    clients: false,
    events: false,
    legalFiles: false,
    systemPresetData: false,
    isComplete: false
  });

  // Update progress
  const updateProgress = (current: string, completed: number, total: number) => {
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    setProgress({
      total,
      completed,
      current,
      percentage
    });
  };

  // Load all cache data
  const loadCache = async () => {
    console.log('🚀 ===== CACHE LOADING INITIATED =====');
    console.log('🔍 Debug - loadCache called');
    console.log('🔍 Debug - User ID:', user?.id);
    console.log('🔍 Debug - Roles count:', roles.length);
    console.log('🔍 Debug - User object:', user);
    console.log('🔍 Debug - Roles array:', roles);
    console.log('🔍 Debug - Current isLoaded state:', isLoaded);
    console.log('🔍 Debug - Current isLoading state:', isLoading);
    
    if (!user?.id || !roles.length) {
      console.log('🚫 Cache loading skipped: No user or roles');
      console.log('🔍 Debug - User ID:', user?.id);
      console.log('🔍 Debug - Roles count:', roles.length);
      return;
    }
    
    if (isLoaded) {
      console.log('⚠️ Debug - Cache already loaded, skipping loadCache');
      console.log('🔍 Debug - Current cache state:', {
        offices: cache.offices.length,
        clients: cache.clients.length,
        events: cache.events.length,
        legalFiles: cache.legalFiles.length,
        roles: cache.roles.length
      });
      return;
    }
    
    if (isLoading) {
      console.log('⚠️ Debug - Cache loading already in progress, skipping loadCache');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Reset detailed loading state
      setDetailedLoading({
        userProfile: false,
        roles: false,
        offices: false,
        clients: false,
        events: false,
        legalFiles: false,
        systemPresetData: false,
        isComplete: false
      });
      
      const totalSteps = 6; // offices, clients, events, legalFiles, systemPresetData, roles
      let completed = 0;

      console.log('🚀 Starting cache loading...');
      console.log('🔍 Debug - User ID:', user.id);
      console.log('🔍 Debug - Roles:', roles.map(r => ({ id: r.id, lawyerName: r.lawyerName, officeName: r.officeName, isActive: !r.isArchived && !r.isDeleted })));

      // Mark user profile as loaded (user is already authenticated)
      setDetailedLoading(prev => ({ ...prev, userProfile: true }));

      // Mark roles as loaded (they're already available)
      setDetailedLoading(prev => ({ ...prev, roles: true }));

      // Load offices
      updateProgress('Loading offices...', completed, totalSteps);
      setDetailedLoading(prev => ({ ...prev, offices: true }));
      const offices = await fetchOfficesByLawyerID(user.id);
      setCache(prev => ({ ...prev, offices }));
      completed++;
      
      // Add delay to make process visible
      console.log('⏳ Debug - Waiting 1 second before loading clients...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      updateProgress('Loading clients...', completed, totalSteps);
      setDetailedLoading(prev => ({ ...prev, clients: true }));

      // Load clients using role IDs (more efficient approach)
      console.log('🔍 Debug - ===== STARTING CLIENTS FETCHING PROCESS =====');
      console.log('🔍 Debug - Fetching clients using role IDs approach');
      
      const roleIds = roles.map(role => role.id);
      console.log('🔍 Debug - Role IDs for client query:', roleIds);
      console.log('🔍 Debug - Role details for client query:', roles.map(r => ({
        id: r.id,
        lawyerName: r.lawyerName,
        officeName: r.officeName,
        isOfficeCreator: r.isOfficeCreator,
        isOfficeAdmin: r.isOfficeAdmin,
        isArchived: r.isArchived,
        isDeleted: r.isDeleted
      })));
      
      console.log('🔍 Debug - Calling fetchClientsByRoleIDs with', roleIds.length, 'role IDs');
      const startTime = Date.now();
      
      const allClients = await fetchClientsByRoleIDs(roleIds);
      
      const endTime = Date.now();
      console.log('🔍 Debug - fetchClientsByRoleIDs completed in', endTime - startTime, 'ms');
      
      console.log('✅ Debug - Total clients fetched by role IDs:', allClients.length);
      console.log('🔍 Debug - Clients summary data:', allClients.map(c => ({ 
        id: c.id, 
        name: c.name, 
        rolesCount: c.rolesIDs?.length || 0,
        rolesIDs: c.rolesIDs,
        mobileNumber: c.mobileNumber,
        email: c.email,
        isArchived: c.isArchived,
        isDeleted: c.isDeleted
      })));
      
      // Analyze client distribution by roles
      console.log('🔍 Debug - Client distribution analysis:');
      roleIds.forEach(roleId => {
        const clientsWithRole = allClients.filter(client => 
          client.rolesIDs?.includes(roleId)
        );
        const role = roles.find(r => r.id === roleId);
        console.log(`🔍 Debug - Role "${role?.officeName}" (${roleId}): ${clientsWithRole.length} clients`);
        if (clientsWithRole.length > 0) {
          console.log('🔍 Debug - Clients for this role:', clientsWithRole.map(c => ({
            id: c.id,
            name: c.name,
            mobileNumber: c.mobileNumber
          })));
        }
      });
      
      console.log('🔍 Debug - Setting clients in cache...');
      setCache(prev => ({ ...prev, clients: allClients }));
      console.log('✅ Debug - ===== CLIENTS FETCHING PROCESS COMPLETED =====');
      completed++;
      
      // Add delay to make process visible
      console.log('⏳ Debug - Waiting 1.5 seconds before loading events...');
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      updateProgress('Loading events...', completed, totalSteps);
      setDetailedLoading(prev => ({ ...prev, events: true }));

      // Load events
      console.log('🔍 Debug - Fetching events for lawyer ID:', user.id);
      const events = await fetchEventsByLawyerID(user.id);
      console.log('✅ Debug - Events fetched:', events.length, 'events');
      console.log('🔍 Debug - Events data:', events.map(e => ({ 
        id: e.id, 
        title: e.legalFileTitle, 
        date: e.date, 
        status: e.status, 
        clientName: e.clientName,
        officeName: e.officeName 
      })));
      setCache(prev => ({ ...prev, events }));
      completed++;
      
      // Add delay to make process visible
      console.log('⏳ Debug - Waiting 1 second before loading legal files...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      updateProgress('Loading legal files...', completed, totalSteps);
      setDetailedLoading(prev => ({ ...prev, legalFiles: true }));

      // Load legal files for current user
      console.log('🔍 Debug - Fetching legal files for user:', user.id);
      const legalFiles = await getLegalFilesByLawyerID(user.id);
      console.log('✅ Debug - Legal files fetched:', legalFiles.length, 'files for user', user.id);
      console.log('🔍 Debug - Legal files data:', legalFiles.map(f => ({ 
        id: f.id, 
        title: f.title, 
        clientID: f.clientID, 
        statusID: f.statusID,
        lawyerID: f.lawyerID
      })));
      setCache(prev => ({ ...prev, legalFiles }));
      completed++;
      
      // Add delay to make process visible
      console.log('⏳ Debug - Waiting 1 second before loading system preset data...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      updateProgress('Loading system preset data...', completed, totalSteps);
      setDetailedLoading(prev => ({ ...prev, systemPresetData: true }));

      // Load system preset data (this will be handled by SystemPresetProvider)
      // For now, we'll set it to null to indicate it's loaded
      // The actual data will be available through SystemPresetContext
      setCache(prev => ({ ...prev, systemPresetData: null }));
      completed++;
      updateProgress('Loading roles...', completed, totalSteps);

      // Set roles
      console.log('🔍 Debug - Setting roles:', roles.length, 'roles');
      console.log('🔍 Debug - Roles data:', roles.map(r => ({ 
        id: r.id, 
        lawyerName: r.lawyerName, 
        officeName: r.officeName,
        isArchived: r.isArchived, 
        isDeleted: r.isDeleted 
      })));
      setCache(prev => ({ ...prev, roles }));
      completed++;
      
      // Calculate usage statistics from cached data
      console.log('📊 Calculating usage statistics...');
      const usageStats = LocalUsageService.calculateUsageStats(
        user.id,
        offices,
        allClients,
        legalFiles,
        roles
      );
      setCache(prev => ({ ...prev, usageStats }));
      console.log('✅ Usage statistics calculated:', usageStats);
      
      // Add final delay to ensure all data is processed
      console.log('⏳ Debug - Final processing delay...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      updateProgress('Cache loaded successfully!', completed, totalSteps);

      // Mark all loading as complete
      setDetailedLoading(prev => ({ ...prev, isComplete: true }));
      setIsLoaded(true);
      console.log('✅ Cache loaded successfully!');
      console.log('📊 Debug - Final cache summary:', {
        offices: offices.length,
        clients: allClients.length,
        events: events.length,
        legalFiles: legalFiles.length,
        roles: roles.length
      });
      console.log('🔍 Debug - Cache state:', {
        isLoaded: true,
        isLoading: false,
        error: null
      });
      console.log('🎉 Debug - ===== ALL CACHE LOADING COMPLETED =====');

    } catch (err) {
      console.error('❌ Error loading cache:', err);
      setError(err instanceof Error ? err.message : 'Failed to load cache');
    } finally {
      setIsLoading(false);
      // Clear progress after a delay
      setTimeout(() => {
        setProgress(prev => ({ ...prev, current: '', percentage: 100 }));
      }, 2000);
    }
  };

  // Refresh cache
  const refreshCache = async () => {
    setIsLoaded(false);
    await loadCache();
  };

  // Invalidate specific cache keys
  const invalidateCache = (keys: (keyof CacheData)[]) => {
    const updates: Partial<CacheData> = {};
    keys.forEach(key => {
      if (key === 'offices') updates.offices = [];
      if (key === 'clients') updates.clients = [];
      if (key === 'events') updates.events = [];
      if (key === 'legalFiles') updates.legalFiles = [];
      if (key === 'systemPresetData') updates.systemPresetData = null;
      if (key === 'roles') updates.roles = [];
    });
    setCache(prev => ({ ...prev, ...updates }));
  };

  // Clear all cache data
  const clearAllCache = () => {
    console.log('🧹 Debug - CacheContext: Clearing all cache data');
    setCache({
      offices: [],
      clients: [],
      events: [],
      legalFiles: [],
      systemPresetData: null,
      roles: [],
      usageStats: null
    });
    setIsLoaded(false);
    setIsLoading(false);
    setError(null);
    setProgress({
      total: 0,
      completed: 0,
      current: '',
      percentage: 0
    });
    setDetailedLoading({
      userProfile: false,
      roles: false,
      offices: false,
      clients: false,
      events: false,
      legalFiles: false,
      systemPresetData: false,
      isComplete: false
    });
    console.log('✅ Debug - CacheContext: All cache data cleared');
  };

  // Data getters
  const getOffices = () => cache.offices;
  
  const getClients = (officeId?: string) => {
    if (!officeId) return cache.clients;
    
    // Find all role IDs that belong to this office (handle data type mismatches)
    const officeRoleIds = cache.roles
      .filter(role => role.officeID === officeId || role.officeID === String(officeId) || String(role.officeID) === officeId)
      .map(role => role.id);
    
    // Filter clients that have any of these role IDs
    return cache.clients.filter(client => 
      client.rolesIDs?.some(roleId => officeRoleIds.includes(roleId))
    );
  };
  
  const getEvents = (filters?: { status?: string; date?: string }) => {
    let filteredEvents = cache.events;
    
    if (filters?.status && filters.status !== 'All Status') {
      const statusMap: { [key: string]: number } = {
        'Upcoming': 1,
        'In Progress': 2,
        'Completed': 3,
        'Cancelled': 4,
        'Expired': 5
      };
      const statusValue = statusMap[filters.status];
      if (statusValue) {
        filteredEvents = filteredEvents.filter(event => event.status === statusValue);
      }
    }
    
    if (filters?.date) {
      filteredEvents = filteredEvents.filter(event => event.date === filters.date);
    }
    
    return filteredEvents;
  };
  
  const getLegalFiles = (clientId?: string) => {
    if (!clientId) return cache.legalFiles;
    return cache.legalFiles.filter(file => file.clientID === clientId);
  };
  
  const getSystemPresetData = () => cache.systemPresetData;
  
  const getRoles = () => cache.roles;

  const getUsageStats = () => cache.usageStats;

  const calculateUsageStats = () => {
    if (!user?.id) return null;
    
    return LocalUsageService.calculateUsageStats(
      user.id,
      cache.offices,
      cache.clients,
      cache.legalFiles,
      cache.roles
    );
  };

  // Data mutations
  const addOffice = (office: Office) => {
    setCache(prev => {
      const newCache = {
        ...prev,
        offices: [...prev.offices, office]
      };
      
      // Recalculate usage stats
      if (user?.id) {
        const usageStats = LocalUsageService.calculateUsageStats(
          user.id,
          newCache.offices,
          newCache.clients,
          newCache.legalFiles,
          newCache.roles
        );
        newCache.usageStats = usageStats;
      }
      
      return newCache;
    });
  };

  const updateOffice = (officeId: string, updates: Partial<Office>) => {
    setCache(prev => ({
      ...prev,
      offices: prev.offices.map(office =>
        office.id === officeId ? { ...office, ...updates } : office
      )
    }));
  };

  const removeOffice = (officeId: string) => {
    setCache(prev => ({
      ...prev,
      offices: prev.offices.filter(office => office.id !== officeId),
      clients: prev.clients.filter(client => !client.rolesIDs?.includes(officeId))
    }));
  };

  const addClient = (client: Client) => {
    setCache(prev => {
      const newCache = {
        ...prev,
        clients: [...prev.clients, client]
      };
      
      // Recalculate usage stats
      if (user?.id) {
        const usageStats = LocalUsageService.calculateUsageStats(
          user.id,
          newCache.offices,
          newCache.clients,
          newCache.legalFiles,
          newCache.roles
        );
        newCache.usageStats = usageStats;
      }
      
      return newCache;
    });
  };

  const updateClient = (clientId: string, updates: Partial<Client>) => {
    setCache(prev => ({
      ...prev,
      clients: prev.clients.map(client =>
        client.id === clientId ? { ...client, ...updates } : client
      )
    }));
  };

  const removeClient = (clientId: string) => {
    setCache(prev => ({
      ...prev,
      clients: prev.clients.filter(client => client.id !== clientId),
      legalFiles: prev.legalFiles.filter(file => file.clientID !== clientId)
    }));
  };

  const addEvent = (event: Event) => {
    setCache(prev => ({
      ...prev,
      events: [...prev.events, event]
    }));
  };

  const updateEvent = (eventId: string, updates: Partial<Event>) => {
    setCache(prev => ({
      ...prev,
      events: prev.events.map(event =>
        event.id === eventId ? { ...event, ...updates } : event
      )
    }));
  };

  const removeEvent = (eventId: string) => {
    setCache(prev => ({
      ...prev,
      events: prev.events.filter(event => event.id !== eventId)
    }));
  };

  const addLegalFile = (legalFile: LegalFile) => {
    setCache(prev => {
      const newCache = {
        ...prev,
        legalFiles: [...prev.legalFiles, legalFile]
      };
      
      // Recalculate usage stats
      if (user?.id) {
        const usageStats = LocalUsageService.calculateUsageStats(
          user.id,
          newCache.offices,
          newCache.clients,
          newCache.legalFiles,
          newCache.roles
        );
        newCache.usageStats = usageStats;
      }
      
      return newCache;
    });
  };

  const updateLegalFile = (legalFileId: string, updates: Partial<LegalFile>) => {
    setCache(prev => ({
      ...prev,
      legalFiles: prev.legalFiles.map(file =>
        file.id === legalFileId ? { ...file, ...updates } : file
      )
    }));
  };

  const removeLegalFile = (legalFileId: string) => {
    setCache(prev => ({
      ...prev,
      legalFiles: prev.legalFiles.filter(file => file.id !== legalFileId)
    }));
  };

  // Cache loading is now triggered manually from login screen
  // No automatic loading on mount

  // Clear cache when user logs out
  useEffect(() => {
    if (!user) {
      console.log('🧹 Debug - CacheContext: User logged out, clearing cache');
      clearAllCache();
    }
  }, [user]);

  // Check when user and roles change
  useEffect(() => {
    console.log('🔍 Debug - CacheContext useEffect triggered');
    console.log('🔍 Debug - User in useEffect:', user?.id);
    console.log('🔍 Debug - Roles in useEffect:', roles.length);
    console.log('🔍 Debug - IsLoaded:', isLoaded);
    console.log('🔍 Debug - IsLoading:', isLoading);
    
    // Auto-load cache when user and roles are available
    if (user?.id && roles.length > 0 && !isLoaded && !isLoading) {
      console.log('🚀 Debug - Conditions met for auto-loading cache');
      console.log('🔍 Debug - User ID:', user.id);
      console.log('🔍 Debug - Roles count:', roles.length);
      console.log('🔍 Debug - Starting automatic cache loading...');
      
      // Load cache automatically
      loadCache();
    }
    
    // Check if cache is already loaded
    if (isLoaded) {
      console.log('🔍 Debug - Cache is already loaded in useEffect');
      console.log('🔍 Debug - Current cache data:', {
        offices: cache.offices.length,
        clients: cache.clients.length,
        events: cache.events.length,
        legalFiles: cache.legalFiles.length,
        roles: cache.roles.length
      });
    }
  }, [user, roles.length, isLoaded, isLoading, loadCache]); // Use roles.length instead of roles array

  // Update system preset data when available
  useEffect(() => {
    // This will be updated by the SystemPresetProvider
  }, []);

  const value: CacheContextType = {
    // Data
    cache,
    isLoaded,
    isLoading,
    error,
    
    // Progress
    progress,
    detailedLoading,
    
    // Actions
    loadCache,
    refreshCache,
    invalidateCache,
    clearAllCache,
    
    // Data getters
    getOffices,
    getClients,
    getEvents,
    getLegalFiles,
    getSystemPresetData,
    getRoles,
    getUsageStats,
    calculateUsageStats,
    
    // Data mutations
    addOffice,
    updateOffice,
    removeOffice,
    addClient,
    updateClient,
    removeClient,
    addEvent,
    updateEvent,
    removeEvent,
    addLegalFile,
    updateLegalFile,
    removeLegalFile
  };

  return (
    <CacheContext.Provider value={value}>
      {children}
    </CacheContext.Provider>
  );
};
